import { ApiRequestError, fetchAPI } from "./api";
import { getUserAccessToken } from "@/lib/hfnauth";
import { logger } from "@/lib/logger";

/**
 * Interface for Tree Reservation Request
 */
export interface TreeReservationRequest {
  dep_type: "PROJECT" | "SPECIES" | "ATTRIBUTE";
  dep_id?: number;
  /** Attribute/campaign id — same value sent as `attributeId` in /donate extras. */
  campaign_id?: number;
  tree_count: number;
  is_geotagged: boolean;
  user_email?: string;
  user_city?: string;
  donation_type?: "SELF" | "GIFT";
  recipients?: {
    name: string;
    email: string;
    phone: string;
    trees: number;
  }[];
}

/**
 * Species breakdown for PROJECT DEP
 */
export interface SpeciesBreakdown {
  species_id: number;
  available: number;
  allocated: number;
}

/**
 * Project breakdown for SPECIES DEP
 */
export interface ProjectBreakdown {
  project_id: number;
  available: number;
  allocated: number;
}

/**
 * Full breakdown for ATTRIBUTE DEP
 */
export interface AttributeBreakdown {
  project_id: number;
  species_id: number;
  available: number;
  allocated: number;
}

/**
 * Success response from reservation API
 */
export interface TreeReservationSuccess {
  success: true;
  reservation_token: string;
  reservation_id: number;
  expires_at: string;
  expires_in_seconds: number;
  message: string;
  donation_type?: "SELF" | "GIFT";
  recipient_count?: number;
  species_breakdown?: SpeciesBreakdown[];
  project_breakdown?: ProjectBreakdown[];
  breakdown?: AttributeBreakdown[];
}

/**
 * Error response from reservation API
 */
export interface TreeReservationError {
  success: false;
  error: string;
  message: string;
  requested?: number;
  available?: number;
}

/**
 * Union type for reservation response
 */
export type TreeReservationResponse =
  | TreeReservationSuccess
  | TreeReservationError;

/**
 * Cancel a tree reservation (releases locked trees immediately)
 * Fire-and-forget safe — call without awaiting on payment failure/dismiss.
 */
export async function cancelReservation(
  reservationToken: string,
): Promise<void> {
  try {
    const accessToken = await getUserAccessToken();
    await fetchAPI(
      `/allocations/reservations/cancel/`,
      {},
      {
        method: "POST",
        body: JSON.stringify({ reservation_token: reservationToken }),
        ...(accessToken && {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }),
      },
    );
  } catch (error) {
    logger.warn("Reservation cancel failed (non-critical):", error);
  }
}

/**
 * Create a tree reservation (lock trees for 15 minutes)
 */
export async function createTreeReservation(
  request: TreeReservationRequest,
): Promise<TreeReservationResponse> {
  try {
    // Token establishes who the donor is; user_email/recipients[] in the
    // body are unrelated (gift recipient details) and stay as-is.
    const accessToken = await getUserAccessToken();
    const data = await fetchAPI<TreeReservationResponse>(
      `/allocations/reservations/create/`,
      {},
      {
        method: "POST",
        body: JSON.stringify(request),
        ...(accessToken && {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }),
      },
    );
    return data;
  } catch (error) {
    if (error instanceof ApiRequestError) {
      const payload = error.payload as
        | {
            error?: string;
            message?: string;
            available?: number;
            requested?: number;
          }
        | undefined;
      return {
        success: false,
        error: payload?.error || "reservation_failed",
        message:
          payload?.message ||
          error.message ||
          "Unable to reserve trees. Please try again.",
        available: payload?.available,
      };
    }

    return {
      success: false,
      error: "network_error",
      message: "Unable to reserve trees. Please try again.",
    };
  }
}
